import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getProblemDetail } from '@/services/problemsApi';
import { getJudgeAvailability } from '@/services/judgeAgentsApi';
import { createSubmission, getSubmissionResult } from '@/services/submissionsApi';
import { getUserFriendlyMessage } from '@/services/apiError';
import { queryKeys } from '@/lib/queryKeys';
import { isSubmissionPending } from '@/lib/submissions';
import type { SubmissionResultSummary, SubmissionStatus } from '@/services/types';
import PageLoading from '@/components/common/PageLoading';
import ContentApiErrorState, { ContentLoadError, ContentNotFound } from '@/components/common/ContentErrorState';
import SectionCard from '@/components/common/SectionCard';
import FormMessage from '@/components/common/FormMessage';
import SubmitResultPanel from '@/components/submissions/SubmitResultPanel';
import SubmissionCaseResults from '@/components/submissions/SubmissionCaseResults';

export default function ProblemDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [code, setCode] = useState('');
  const [activeSubmissionId, setActiveSubmissionId] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState('');
  const [submitMessage, setSubmitMessage] = useState('');

  const {
    data: problem,
    isLoading,
    isError,
    error: problemError,
    refetch,
  } = useQuery({
    queryKey: queryKeys.problems.detail(id ?? ''),
    queryFn: () => getProblemDetail(id ?? ''),
    enabled: Boolean(id),
    retry: 1,
  });

  const problemId = problem?.id;
  const problemStarterCode = problem?.starterCode ?? '';

  const judgeAvailabilityQuery = useQuery({
    queryKey: queryKeys.judgeAgents.availability,
    queryFn: getJudgeAvailability,
    retry: 1,
    refetchInterval: 10_000,
  });

  useEffect(() => {
    if (!problemId) {
      return;
    }

    setCode(problemStarterCode);
    setSubmitError('');
    setSubmitMessage('');
    setActiveSubmissionId(null);
  }, [problemId, problemStarterCode]);

  const submitMutation = useMutation({
    mutationFn: (nextCode: string) => {
      return createSubmission({
        problemId: id ?? '',
        code: nextCode,
      });
    },
    onSuccess: (submission) => {
      setSubmitError('');
      setSubmitMessage(submission.message || '提交成功，正在判题。');
      setActiveSubmissionId(submission.id);
      queryClient.invalidateQueries({ queryKey: queryKeys.problems.root });
      queryClient.invalidateQueries({ queryKey: queryKeys.submissions.root });
    },
    onError: (error: unknown) => {
      setSubmitError(getUserFriendlyMessage(error, '提交失败，请稍后重试'));
      setSubmitMessage('');
    },
  });

  const submissionResultQuery = useQuery({
    queryKey: queryKeys.submissions.result(activeSubmissionId ?? 'pending'),
    queryFn: () => getSubmissionResult(activeSubmissionId ?? ''),
    enabled: Boolean(activeSubmissionId),
    retry: 1,
    refetchOnWindowFocus: false,
    refetchInterval: (query) => {
      const data = query.state.data as SubmissionResultSummary | undefined;
      const status = data?.status ?? submitMutation.data?.status;
      return status && isSubmissionPending(status) ? 2500 : false;
    },
  });

  const currentSubmissionStatus = useMemo<SubmissionStatus | null>(() => {
    return submissionResultQuery.data?.status ?? submitMutation.data?.status ?? null;
  }, [submissionResultQuery.data?.status, submitMutation.data?.status]);
  const judgeUnavailable = judgeAvailabilityQuery.data?.available === false;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) {
      return;
    }

    if (judgeUnavailable) {
      setSubmitError('服务不可用');
      return;
    }

    if (!code.trim()) {
      setSubmitError('代码不能为空');
      return;
    }

    setSubmitError('');
    submitMutation.mutate(code);
  };

  if (isLoading) {
    return <PageLoading message='题目加载中…' />;
  }

  if (!id) {
    return (
      <div className='max-w-[980px] mx-auto px-6 py-12'>
        <ContentNotFound />
      </div>
    );
  }

  if (isError) {
    return (
      <div className='max-w-[980px] mx-auto px-6 py-12'>
        <ContentApiErrorState error={problemError} onRetry={() => void refetch()} />
      </div>
    );
  }

  if (!problem) {
    return (
      <div className='max-w-[980px] mx-auto px-6 py-12'>
        <ContentLoadError onRetry={() => void refetch()} />
      </div>
    );
  }

  return (
    <div className='max-w-[980px] mx-auto px-6 py-12'>
      {/* Back link */}
      <Link to='/problems' className='btn-ghost mb-6 inline-flex'>
        ‹ 返回题目库
      </Link>

      <div className='grid grid-cols-1 lg:grid-cols-2 gap-5 mt-4'>
        <SectionCard className='p-7'>
          <h1
            className='font-semibold text-apple-near-black mb-5'
            style={{ fontSize: '1.5rem', lineHeight: 1.14, letterSpacing: '-0.02em' }}
          >
            {problem.title}
          </h1>

          <h3
            className='font-medium text-apple-near-black mb-2'
            style={{ fontSize: '0.9375rem' }}
          >
            题目描述
          </h3>
          <p className='text-apple-gray mb-6' style={{ fontSize: '0.9375rem', lineHeight: 1.6 }}>
            {problem.description}
          </p>

          {problem.testCases.length > 0 && (
            <div>
              <h3
                className='font-medium text-apple-near-black mb-3'
                style={{ fontSize: '0.9375rem' }}
              >
                示例用例
              </h3>
              <div className='space-y-3'>
                {problem.testCases.slice(0, 3).map((item, i) => (
                  <div key={i} className='bg-apple-light-gray rounded-xl p-4 font-mono text-[0.8125rem] text-apple-near-black space-y-1'>
                    <div><span className='text-apple-gray'>输入：</span>{item.input}</div>
                    <div><span className='text-apple-gray'>输出：</span>{item.expectedOutput}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </SectionCard>

        <SectionCard className='p-7 flex flex-col'>
          <h2
            className='font-semibold text-apple-near-black mb-4'
            style={{ fontSize: '1.1875rem', lineHeight: 1.19 }}
          >
            代码编辑器
          </h2>
          <form onSubmit={handleSubmit} className='flex flex-col flex-1'>
            <textarea
              className='flex-1 min-h-[280px] w-full rounded-xl border border-apple-border bg-apple-light-gray px-4 py-3 font-mono text-[0.875rem] text-apple-near-black placeholder:text-apple-gray focus:outline-none focus:ring-2 focus:ring-apple-blue focus:border-transparent resize-none mb-4'
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder='# 在这里编写 Python 代码…'
              spellCheck={false}
            />
            <button
              type='submit'
              className='btn btn-primary w-full justify-center'
              disabled={submitMutation.isPending || !code.trim() || judgeUnavailable}
            >
              {submitMutation.isPending ? '提交中…' : '提交代码'}
            </button>
          </form>

          {judgeUnavailable ? <div className='mt-4'><FormMessage tone='error' message='服务不可用' /></div> : null}
          {submitError ? <div className='mt-4'><FormMessage tone='error' message={submitError} /></div> : null}
          {submitMessage ? <div className='mt-4'><FormMessage tone='success' message={submitMessage} /></div> : null}

          {activeSubmissionId && currentSubmissionStatus ? (
            <div className='mt-4 space-y-3'>
              <SubmitResultPanel status={currentSubmissionStatus} result={submissionResultQuery.data} />
              <div className='flex items-center gap-3'>
                <Link to={`/submissions/${activeSubmissionId}`} className='btn btn-secondary'>
                  查看提交详情
                </Link>
                <button
                  type='button'
                  className='btn btn-primary'
                  onClick={() => void submissionResultQuery.refetch()}
                  disabled={submissionResultQuery.isFetching}
                >
                  {submissionResultQuery.isFetching ? '刷新中…' : '刷新结果'}
                </button>
              </div>
            </div>
          ) : null}
        </SectionCard>
      </div>

      {activeSubmissionId && currentSubmissionStatus && !isSubmissionPending(currentSubmissionStatus) ? (
        <div className='mt-5'>
          <SubmissionCaseResults result={submissionResultQuery.data} />
        </div>
      ) : null}
    </div>
  );
}
